/* eslint-disable max-len */
import React, { useContext } from 'react';
import {
  bool as boolType,
  any,
  string as stringType
} from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  bool,
  extend,
  string,
  shape,
  QueryProvider,
  QueryContext,
  params
} from '@thd-nucleus/data-sources';
import { Carousel } from '@one-thd/sui-carousel';
import classNames from 'classnames';
import { LoadingPlaceholder } from '../core/LoadingPlaceholder';
import { CarouselHeader } from '../core/CarouselHeader';
import {
  getSkippers, isAppliance, getLoyaltyMembershipInput, getUdsProductTitle, getUdsProductBreakPoint,
  hasBadges
} from '../utils/helpers';
import { FallbackCard } from '../core/FallbackCard';
import { RecsProductPod } from '../core/RecsProductPod';

// the QueryProvider on line 54 doesnt get access to ProductPod because data?.products is empty at first
// this is what hoist() was originally intended for but the problem with hoist() is that it mutates
// components and runs into issues when they are in multiple places
// eslint-disable-next-line react/prop-types
const Wrapper = ({ children }) => children;
Wrapper.dataModel = extend({
  product: params({ itemId: string().isRequired() }).shape({
    dataSource: string()
  })
}, RecsProductPod);
const ProductCarousel = (props) => {

  const { channel } = useContext(ExperienceContext);
  const { defaultVariables } = useContext(QueryContext) || {};
  const isMobile = channel === 'mobile';
  const {
    data,
    hideTitle,
    loading,
    scheme,
    showLoading,
    storeId,
    showFallback,
    fallbackInfo,
    userId,
    subtitle,
    brandTitleMaxLine,
    hideATC,
    hideFavorites,
    hideBadge,
    hideRating,
    showSubscribeAndSave,
    membershipInformation,
    zipCode,
    slidesPer,
    noATCFulfillment,
    hideControls,
    analyticsAnchorProductSku,
    parent,
    slidesToShow
  } = props;

  const qtxDefaultVariables = {
    storeId,
    zipCode,
    installationServices: false,
    loyaltyMembershipInput: getLoyaltyMembershipInput(membershipInformation),
    ...getSkippers({ skipSubscribeAndSave: !showSubscribeAndSave }),
    ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean' && {
      isBrandPricingPolicyCompliant: defaultVariables.current.isBrandPricingPolicyCompliant
    })
  };

  const { products, metadata } = data || {};

  const classes = classNames('sui-grid', 'sui-gap-4', 'sui-grid-cols-1');

  let title = getUdsProductTitle(metadata);

  let breakpoints = ({});
  breakpoints = getUdsProductBreakPoint(slidesToShow, slidesPer);

  let placeholderTxtLines;
  if (!isMobile) {
    placeholderTxtLines = 7;
  } else {
    placeholderTxtLines = hideATC ? 4 : 6;
  }

  const getScheme = (schemeName) => {
    return (schemeName === 'products_b2b' || schemeName === 'products') ? 'todays' : schemeName;
  };

  const badgeCheck = hasBadges(products, hideBadge);

  return (
    <>
      {showLoading && loading && (
        showFallback && !userId && !products
          ? (
            <FallbackCard
              imageUrl={fallbackInfo.imageUrl}
              header={fallbackInfo.header}
              subheader={fallbackInfo.subheader}
              footerUrl={fallbackInfo.footerUrl}
              footer={fallbackInfo.footer}
            />
          )
          : <LoadingPlaceholder txtPlaceholderLines={placeholderTxtLines} hideTitle={hideTitle} />
      )}
      {products?.length > 0 && (
        <>
          {!hideTitle && (
            <CarouselHeader title={title} subtitle={subtitle} />
          )}

          <div className={classes}>
            <QueryProvider
              dataSource={scheme}
              cacheKey={`${scheme}-recommendations-carousel`}
              defaultVariables={qtxDefaultVariables}
            >
              <Carousel
                breakpoints={breakpoints}
                hideControls={hideControls}
                disableMargin
                layout="full-bleed"
                containerId={scheme}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(isMobile ? ({ slidesPerGroup: 2 }) : {})}
              >
                {products.map((dynamicProduct, idx) => {
                  const { product } = dynamicProduct;
                  const { itemId } = product;
                  const { preferredPriceFlag } = product?.pricing;
                  const { canonicalUrl } = product?.identifiers || {};
                  return (
                    <Wrapper key={idx}>
                      <RecsProductPod
                        itemId={itemId}
                        key={itemId}
                        position={idx}
                        storeId={storeId}
                        scheme={getScheme(scheme)}
                        anchorProduct={analyticsAnchorProductSku}
                        parent={parent || `recs-product-pod-carousel-${scheme}`}
                        hideFavorites={hideFavorites}
                        hideBadge={badgeCheck}
                        hideATC={hideATC}
                        hideRating={hideRating}
                        preferredPriceFlag={preferredPriceFlag}
                        hideSavingsText={!preferredPriceFlag}
                        brandTitleMaxLine={brandTitleMaxLine}
                        strategy={dynamicProduct?.strategy}
                        noATCFulfillment={isAppliance(product) ? false : noATCFulfillment}
                        url={canonicalUrl}
                      />
                    </Wrapper>
                  );
                }
                )}
              </Carousel>
            </QueryProvider>
          </div>
        </>
      )}
    </>
  );
};

const productModel = extend({}, {
  itemId: string(),
  dataSource: string()
}, RecsProductPod.dataModel.product);

export const udsWrappedProduct = shape({
  category: string(),
  description: string(),
  idEndDate: string(),
  idPromoDetails: string(),
  idStartDate: string(),
  strategy: string(),
  isAnchor: bool(),
  product: params({
    dataSource: string()
  })
    .shape(productModel),
  slot: shape({
    description: string(),
    isRequired: bool(),
    name: string()
  })
});

ProductCarousel.dataModel = extend(Wrapper, udsWrappedProduct);

ProductCarousel.displayName = 'ProductCarousel';

ProductCarousel.propTypes = {
  ...RecsProductPod.propTypes,
  // eslint-disable-next-line react/forbid-prop-types
  data: any,
  loading: boolType,
  hideTitle: boolType,
  scheme: stringType,
  showLoading: boolType,
  storeId: stringType.isRequired,
  subtitle: stringType,
  hideATC: boolType,
  hideFavorites: boolType,
  hideBadge: boolType,
  keepAddToCart: boolType,
  analyticsAnchorProductSku: stringType,
  hideRating: boolType,
  noATCFulfillment: boolType,
  hideControls: boolType
};

ProductCarousel.defaultProps = {
  hideTitle: false,
  scheme: 'drecs',
  showLoading: true,
  loading: false,
  data: null,
  subtitle: null,
  hideBadge: false,
  hideATC: false,
  hideFavorites: true,
  keepAddToCart: null,
  analyticsAnchorProductSku: '',
  hideRating: false,
  noATCFulfillment: true,
  hideControls: false
};

export { ProductCarousel };
